import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MapPin, CheckCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getServices, getCompanyInfo, getGeoPages } from "../services/api";
import NotFoundPage from "./NotFoundPage";

// Mapping of nearby service areas for internal linking
const nearbyAreasMap = {
  'thunder-bay': ['murillo', 'rosslyn', 'kakabeka-falls', 'slate-river', 'shuniah', 'oliver-paipoonge'],
  'murillo': ['thunder-bay', 'rosslyn', 'kakabeka-falls', 'nolalu', 'gillies'],
  'rosslyn': ['thunder-bay', 'murillo', 'slate-river', 'pass-lake', 'shuniah'],
  'kakabeka-falls': ['thunder-bay', 'murillo', 'nolalu', 'gillies', 'oliver-paipoonge'],
  'slate-river': ['thunder-bay', 'rosslyn', 'pass-lake', 'shuniah'],
  'pass-lake': ['rosslyn', 'slate-river', 'shuniah', 'nipigon'],
  'shuniah': ['thunder-bay', 'rosslyn', 'slate-river', 'pass-lake', 'oconnor'],
  'oconnor': ['shuniah', 'gillies', 'nolalu', 'conmee', 'kaministiquia'],
  'gillies': ['murillo', 'kakabeka-falls', 'oconnor', 'nolalu'],
  'nolalu': ['murillo', 'kakabeka-falls', 'oconnor', 'gillies', 'oliver-paipoonge'],
  'conmee': ['oconnor', 'kaministiquia', 'gillies'],
  'oliver-paipoonge': ['thunder-bay', 'kakabeka-falls', 'nolalu', 'kaministiquia'],
  'kaministiquia': ['oliver-paipoonge', 'conmee', 'oconnor', 'atikokan'],
  'nipigon': ['pass-lake', 'red-rock', 'schreiber', 'dorion'],
  'red-rock': ['nipigon', 'schreiber', 'terrace-bay', 'dorion'],
  'schreiber': ['nipigon', 'red-rock', 'terrace-bay', 'marathon'],
  'terrace-bay': ['red-rock', 'schreiber', 'marathon'],
  'marathon': ['schreiber', 'terrace-bay'],
  'kenora': ['sioux-lookout', 'dryden', 'red-lake'],
  'sioux-lookout': ['kenora', 'red-lake', 'dryden'],
  'red-lake': ['kenora', 'sioux-lookout'],
  'dryden': ['kenora', 'sioux-lookout', 'ignace'],
  'fort-frances': ['atikokan', 'kenora'],
  'atikokan': ['fort-frances', 'kaministiquia', 'ignace'],
  'dorion': ['nipigon', 'red-rock', 'thunder-bay'],
  'pearl': ['ignace', 'savanne', 'dryden'],
  'savanne': ['pearl', 'fort-frances', 'atikokan'],
  'ignace': ['dryden', 'pearl', 'atikokan']
};

const GeoPage = () => {
  const { city } = useParams();
  const cityName = city.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  
  const [services, setServices] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({ phone: "1-888-907-3777" });
  const [cityStatus, setCityStatus] = useState({ city, value: 'loading' });

  useEffect(() => {
    let current = true;
    getGeoPages().then((pages) => {
      if (current) setCityStatus({ city, value: pages.some((page) => page.slug === city) ? 'available' : 'not-found' });
    }).catch(() => { if (current) setCityStatus({ city, value: 'unavailable' }); });
    return () => { current = false; };
  }, [city]);

  useEffect(() => {
    // Fetch services and company info
    Promise.all([getServices(), getCompanyInfo()])
      .then(([servicesData, companyInfoData]) => {
        setServices(servicesData);
        setCompanyInfo(companyInfoData);
      })
      .catch(error => console.error("Error fetching data:", error));
  }, []);

  // Get nearby areas for current city
  const nearbyAreas = nearbyAreasMap[city] || [];
  const formatCityName = (slug) => slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  if (cityStatus.city !== city || cityStatus.value === 'loading') {
    return <div className="min-h-screen flex items-center justify-center" data-testid="service-area-loading">Loading...</div>;
  }
  if (cityStatus.value === 'not-found') return <NotFoundPage />;
  if (cityStatus.value === 'unavailable') return (
    <div>
      <SEO title="Service Area Information Unavailable | Groundhogs Septics" description="Please contact Groundhogs Septics to confirm service availability." robots="noindex, follow" />
      <Header />
      <section className="pt-32 pb-16 px-6 text-center" data-testid="service-area-error">
        <h1 data-testid="service-area-error-heading">Service Area Information Unavailable</h1>
        <p className="my-6" data-testid="service-area-error-message">Please contact us to confirm service availability.</p>
        <Link className="gh-button" to="/contact" data-testid="service-area-error-contact">Contact Request</Link>
      </section>
      <Footer />
    </div>
  );

  return (
    <div>
      <SEO 
        title={`Septic Installation ${cityName} | Fast Service`}
        description={`Professional septic installation in ${cityName}, ON. 3-day average installs, 100% permit approval. Call 1-888-907-3777 for free estimate.`}
        keywords={`septic installation ${cityName}, septic ${cityName}`}
      />
      <Header />

      {/* Hero Section */}
      <section
        className="gh-intro gh-photo-intro pt-32 pb-16"
        style={{
          backgroundImage: "url('/seo-images/f69s3hj7_sep.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative"
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(28, 43, 58, 0.85)" }} />
        <div className="gh-photo-intro-copy relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white" data-reveal>
          <h1 className="text-5xl font-bold mb-4">
            Septic Installation in {cityName}, Ontario
          </h1>
          <p className="text-2xl mb-6" style={{ color: "var(--primary-orange)" }}>
            Professional Septic System Installation & Replacement
          </p>
          <p className="text-lg max-w-3xl mx-auto mb-8">
            Groundhogs™ delivers expert septic installation services throughout {cityName} and surrounding areas. 
            Engineered solutions for Northwestern Ontario&apos;s unique soil conditions and climate.
          </p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center space-x-2 px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
            style={{ backgroundColor: "var(--primary-orange)" }}
          >
            <Phone size={24} />
            <span>{companyInfo.phone} - Free Virtual Estimate</span>
          </a>
        </div>
      </section>

      {/* Local Expertise Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Why Choose Groundhogs™ in {cityName}?
              </h2>
              <p className="mb-6" style={{ color: "var(--text-light)" }}>
                With over 15 years of experience installing septic systems throughout Northwestern Ontario, 
                we understand the specific challenges of {cityName}&apos;s soil composition, water table levels, 
                and harsh winter conditions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
                  <div>
                    <strong>Local Expertise:</strong> We know {cityName}&apos;s soil types and permit requirements
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
                  <div>
                    <strong>Complete Permit Handling:</strong> We manage all paperwork and inspections
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
                  <div>
                    <strong>Eljen Certified:</strong> Advanced mound system specialists
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={24} style={{ color: "var(--primary-orange)" }} className="flex-shrink-0 mt-1" />
                  <div>
                    <strong>5-Year Warranty:</strong> All installations backed by comprehensive warranty
                  </div>
                </li>
              </ul>
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="/seo-images/jgz2o8et_sep1.webp"
                alt={`Septic installation in ${cityName}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services in City */}
      <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-center" style={{ color: "var(--dark-navy)" }}>
            Septic Services in {cityName}
          </h2>
          <p className="text-center mb-12 text-lg" style={{ color: "var(--text-light)" }}>
            Complete installation solutions for residential and commercial properties
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg" style={{ transition: "box-shadow 0.3s ease" }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                  {service.title}
                </h3>
                <p className="mb-4" style={{ color: "var(--text-light)" }}>
                  {service.description}
                </p>
                <Link
                  to={`/services/${service.slug}`}
                  className="inline-block px-6 py-2 rounded-md font-medium text-white hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-orange)" }}
                >
                  Learn More
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
            Septic Installation Guide for {cityName} Homeowners
          </h2>
          
          <div className="prose max-w-none">
            <h3 className="text-2xl font-semibold mb-3" style={{ color: "var(--dark-navy)" }}>
              Understanding {cityName}&apos;s Soil Conditions
            </h3>
            <p className="mb-6" style={{ color: "var(--text-dark)" }}>
              The soil composition in {cityName} and surrounding Northwestern Ontario areas presents unique 
              challenges for septic system installation. Clay-heavy soils, rocky terrain, and high water tables 
              require expert assessment and proper system design to ensure long-term performance.
            </p>

            <h3 className="text-2xl font-semibold mb-3" style={{ color: "var(--dark-navy)" }}>
              Traditional vs. Eljen Mound Systems
            </h3>
            <p className="mb-6" style={{ color: "var(--text-dark)" }}>
              For {cityName} properties with challenging site conditions, Eljen mound systems offer superior 
              performance. These advanced systems handle high water tables and difficult percolation rates better 
              than traditional systems, making them ideal for many Northwestern Ontario locations.
            </p>

            <h3 className="text-2xl font-semibold mb-3" style={{ color: "var(--dark-navy)" }}>
              Permits and Regulations
            </h3>
            <p className="mb-6" style={{ color: "var(--text-dark)" }}>
              All septic installations in {cityName} must comply with Ontario Building Code requirements. 
              Groundhogs™ handles all permit applications, site evaluations, and final inspections, ensuring 
              your installation meets or exceeds all regulatory standards.
            </p>

            <h3 className="text-2xl font-semibold mb-3" style={{ color: "var(--dark-navy)" }}>
              Weather Considerations
            </h3>
            <p className="mb-6" style={{ color: "var(--text-dark)" }}>
              Northwestern Ontario&apos;s freeze-thaw cycles require proper system design and installation depth. 
              Our installations account for frost penetration levels specific to the {cityName} region, 
              protecting your investment through harsh winter conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Nearby Service Areas Section */}
      {nearbyAreas.length > 0 && (
        <section className="py-16" style={{ backgroundColor: "var(--light-gray)" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-4 text-center" style={{ color: "var(--dark-navy)" }}>
              Septic Installation in Nearby Communities
            </h2>
            <p className="text-center mb-8 text-lg" style={{ color: "var(--text-light)" }}>
              We also proudly serve these communities near {cityName}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {nearbyAreas.map((areaSlug) => (
                <Link
                  key={areaSlug}
                  to={`/service-areas/${areaSlug}`}
                  className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl text-center transition-all hover:scale-105"
                  style={{ textDecoration: 'none' }}
                >
                  <MapPin size={24} style={{ color: "var(--primary-orange)" }} className="mx-auto mb-2" />
                  <h3 className="font-semibold" style={{ color: "var(--dark-navy)" }}>
                    {formatCityName(areaSlug)}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: "var(--text-light)" }}>
                    Septic Services
                  </p>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/service-areas"
                className="inline-block px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                View All Service Areas
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: "var(--primary-orange)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Install Your Septic System in {cityName}?
          </h2>
          <p className="text-xl mb-8">
            Get your free virtual estimate today. We serve all of {cityName} and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 rounded-md font-bold bg-white hover:opacity-90"
              style={{ color: "var(--primary-orange)" }}
            >
              <Phone size={24} />
              <span>{companyInfo.phone}</span>
            </a>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 rounded-md font-bold border-2 border-white hover:bg-white hover:text-orange-600"
              style={{ transition: "background-color 0.3s ease, color 0.3s ease" }}
            >
              Request Free Estimate
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GeoPage;